import { ContentClassEnum } from '@videoblocks/content-service-client';
import { SearchAudioMediaTypeEnum } from '@videoblocks/search-gateway-client';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import {
    AudioSearchFilterContentTypes,
    SearchFilterContentTypes,
    SearchFilterMediaTypes,
    SearchOrigins,
    SearchUrlParamKeys,
} from './types';

export const AUDIO_SEARCH_ROUTE = '/audio/search';
export const IMAGE_SEARCH_ROUTE = '/images/search';
export const VIDEO_SEARCH_ROUTE = '/:path(all-video/.*|motion-graphics/.*|video/.*|templates/.*)';

const logger = LoggerFactory.create('SearchUtils');
/**
 * converts a singular comma separated search param value into an array
 */
export const getArrayValueFromSearchParam = (paramValue: string | null, delimiter: string = '_'): string[] => {
    if (!paramValue) return [];
    return paramValue.split(delimiter).map((v) => v?.trim());
};

/**
 * The two content classes conversational search can be biased toward. Mirrors the
 * `contentClass` enum on the conv-search-api ChatStreamRequest (video | audio); images
 * are intentionally excluded since conversational search does not support them.
 */
export type ConversationalSearchContentClass = 'video' | 'audio' | 'image';

const AUDIO_CONTENT_TYPES = new Set<string>(Object.values(AudioSearchFilterContentTypes));

/**
 * Maps the content type a search was initiated from to the conversational-search content
 * class used to bias query rewriting. Audio content types map to 'audio'; everything else
 * (video, and any non-audio type) defaults to 'video'.
 */
export const getConversationalSearchContentClass = (
    contentType: SearchFilterContentTypes,
): ConversationalSearchContentClass => (AUDIO_CONTENT_TYPES.has(contentType) ? 'audio' : 'video');

/**
 *  converts an array of strings into a delimited string
 */
export const getParamValueFromArray = (array: string[] | null, delimiter: string = '_'): string => {
    if (!array || !Array.isArray(array) || !array.length) return '';
    return array.join(delimiter);
};

/**
 * removes a single value from a comma separated search param value
 */
export const removeArrayValueFromSearchParam = (
    paramValue: string,
    valueToRemove: string,
    delimiter: string = '_',
): string => {
    if (paramValue.split(delimiter).length > 1) {
        const newValues = paramValue
            .split(delimiter)
            .filter((v) => v !== valueToRemove)
            .join(delimiter);
        return newValues;
    }

    return '';
};

/**
 * adds a new value to a delimited string array value
 */
export const addArrayValueInSearchParams = (
    currentParamValue: string,
    newValues: string[],
    delimiter: string = '_',
): string => {
    const currentValues = getArrayValueFromSearchParam(currentParamValue, delimiter);

    for (const newValue of newValues) {
        if (!currentValues.includes(newValue)) {
            currentValues.push(newValue);
        }
    }

    return getParamValueFromArray(currentValues, delimiter);
};

export interface SearchUrlProps {
    contentType: SearchFilterContentTypes;
    searchOrigin?: SearchOrigins;
    searchRoute?: string;
    searchTerm?: string;
    mediaType?: SearchFilterMediaTypes;
    categoryUrlIds?: string[];
    artistIds?: string[];
    searchSimilarId?: number;
    usageRights?: string;
}
/**
 *
 * @param props contentType and searchOrigin are required
 * @param currentParams for merging with existing search params
 * @returns new search url
 */
export function getSearchUrl(props: SearchUrlProps, currentParams?: URLSearchParams): string {
    const newProps: SearchUrlProps = { ...props };
    newProps.searchRoute = getSearchRoute(newProps);
    const searchPath = getSearchPath(newProps);
    const searchParams = getSearchParams(newProps, currentParams);

    return Array.from(searchParams).length > 0 ? `${searchPath}?${searchParams.toString()}` : searchPath;
}

// searchRoute is one of the *_SEARCH_ROUTE constant values above
export function getSearchRoute({ searchRoute, searchTerm, contentType }: SearchUrlProps): string {
    if (searchRoute) {
        return searchRoute;
    } else if (isAssetId(searchTerm)) {
        return getSearchRouteByAssetId(searchTerm);
    } else {
        return getSearchRouteByContentType(contentType);
    }
}

// searchPath is the actual url path we generate
export function getSearchPath({ searchRoute, contentType, searchTerm }: SearchUrlProps): string {
    let searchPath = isFlatUrl(searchRoute) ? `/${getMediaTypePath(contentType)}/search` : searchRoute;

    if (searchTerm) {
        const cleanedTerm = searchTerm.replace(/%20|%2520|%2b| /g, '-');
        searchPath += `/${cleanedTerm}`;
    }

    return searchPath as string;
}

// TODO: support more search filter params
export function getSearchParams(
    {
        searchOrigin,
        mediaType,
        contentType,
        searchRoute,
        categoryUrlIds,
        searchSimilarId,
        usageRights,
        artistIds,
    }: SearchUrlProps,
    currentParams?: URLSearchParams,
): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (mediaType) {
        searchParams.append(SearchUrlParamKeys.MEDIA_TYPE, mediaType);
    } else if (!isFlatUrl(searchRoute) && contentType) {
        const mediaType = getMediaTypeParam(contentType);
        if (mediaType) {
            searchParams.append(SearchUrlParamKeys.MEDIA_TYPE, mediaType);
        }
    }

    if (categoryUrlIds) {
        const currentParamValue = currentParams?.get(SearchUrlParamKeys.CATEGORIES) || '';
        const newCategoryUrlIds = addArrayValueInSearchParams(currentParamValue, categoryUrlIds);
        searchParams.append(SearchUrlParamKeys.CATEGORIES, newCategoryUrlIds);
    }
    if (searchSimilarId) {
        searchParams.append(SearchUrlParamKeys.SEARCH_SIMILAR_ID, searchSimilarId.toString());
    }
    if (usageRights && usageRights !== 'all') {
        searchParams.append(SearchUrlParamKeys.USAGE_RIGHTS, usageRights);
    }

    if (artistIds) {
        const currentParamValue = currentParams?.get(SearchUrlParamKeys.ARTIST_IDS) || '';
        const newArtistIds = addArrayValueInSearchParams(currentParamValue, artistIds);
        searchParams.append(SearchUrlParamKeys.ARTIST_IDS, newArtistIds);
    }

    if (searchOrigin) {
        searchParams.append(SearchUrlParamKeys.SEARCH_ORIGIN, searchOrigin);
    }
    return searchParams;
}

const isFlatUrl = (searchRoute?: string) => searchRoute === VIDEO_SEARCH_ROUTE;

export function getMediaTypeParam(contentType: SearchFilterContentTypes): SearchFilterMediaTypes | '' {
    switch (contentType) {
        case SearchFilterContentTypes.All_audio_content_type:
        case SearchFilterContentTypes.All_videos_content_type:
        case SearchFilterContentTypes.All_images_content_type:
            return '';
        case SearchFilterContentTypes.Music:
            return SearchFilterMediaTypes.Music;
        case SearchFilterContentTypes.Sound_effects:
            return SearchFilterMediaTypes.Sound_effects;
        case SearchFilterContentTypes.Photos:
            return SearchFilterMediaTypes.Photos;
        case SearchFilterContentTypes.Snapshots:
            return SearchFilterMediaTypes.Snapshots;
        case SearchFilterContentTypes.Vectors:
            return SearchFilterMediaTypes.Vectors;
        case SearchFilterContentTypes.Footage:
            return SearchFilterMediaTypes.Footage;
        default:
            return '';
    }
}

/**
 * Get media type for flat search urls. Currently only used for video searches
 * e.g. /{mediaType}/search/{keyword}
 */
function getMediaTypePath(contentType: SearchFilterContentTypes): string {
    switch (contentType) {
        case SearchFilterContentTypes.Footage:
            return 'video';
        case SearchFilterContentTypes.Motion_bgs:
            return 'motion-graphics';
        case SearchFilterContentTypes.Templates:
            return 'templates';
        case SearchFilterContentTypes.Premiere_pro_templates:
            return 'templates/premiere-pro-templates';
        case SearchFilterContentTypes.After_effects:
            return 'templates/after-effects-templates';
        case SearchFilterContentTypes.Davinci_resolve:
            return 'templates/davinci-resolve-templates';
        case SearchFilterContentTypes.Apple_motion:
            return 'templates/apple-motion-templates';
        default:
            return 'all-video';
    }
}

function getSearchRouteByContentType(contentType: SearchFilterContentTypes): string {
    switch (contentType) {
        case SearchFilterContentTypes.All_videos_content_type:
        case SearchFilterContentTypes.Footage:
        case SearchFilterContentTypes.Motion_bgs:
        case SearchFilterContentTypes.Templates:
        case SearchFilterContentTypes.Premiere_pro_templates:
        case SearchFilterContentTypes.After_effects:
        case SearchFilterContentTypes.Davinci_resolve:
        case SearchFilterContentTypes.Apple_motion:
            return VIDEO_SEARCH_ROUTE;
        case SearchFilterContentTypes.All_audio_content_type:
        case SearchFilterContentTypes.Music:
        case SearchFilterContentTypes.Sound_effects:
            return AUDIO_SEARCH_ROUTE;
        case SearchFilterContentTypes.All_images_content_type:
        case SearchFilterContentTypes.Illustrations:
        case SearchFilterContentTypes.Photos:
        case SearchFilterContentTypes.Vectors:
        case SearchFilterContentTypes.Snapshots:
            return IMAGE_SEARCH_ROUTE;
        default:
            logger.warn({ contentType }, 'No search route found for content type: %s', contentType);
            return '';
    }
}

export function getContentClassByContentType(contentType: SearchFilterContentTypes): ContentClassEnum {
    switch (contentType) {
        case SearchFilterContentTypes.All_audio_content_type:
        case SearchFilterContentTypes.Music:
        case SearchFilterContentTypes.Sound_effects:
            return 'audio';
        case SearchFilterContentTypes.All_images_content_type:
        case SearchFilterContentTypes.Illustrations:
        case SearchFilterContentTypes.Photos:
        case SearchFilterContentTypes.Vectors:
        case SearchFilterContentTypes.Snapshots:
            return 'image';
        case SearchFilterContentTypes.All_videos_content_type:
        case SearchFilterContentTypes.Footage:
        case SearchFilterContentTypes.Motion_bgs:
        case SearchFilterContentTypes.Templates:
        case SearchFilterContentTypes.Premiere_pro_templates:
        case SearchFilterContentTypes.After_effects:
        case SearchFilterContentTypes.Davinci_resolve:
        case SearchFilterContentTypes.Apple_motion:
        default:
            return 'video';
    }
}

const ASSET_ID_PATTERN = /^SB([VIA])-\d{9,}$/i;

export const getAssetTypeFromId = (assetId = ''): string => ASSET_ID_PATTERN.exec(assetId)?.[1] ?? '';
export const isAssetId = (searchTerm = ''): boolean => !!getAssetTypeFromId(searchTerm);

export function getSearchRouteByAssetId(assetId = ''): string {
    const assetType = getAssetTypeFromId(assetId);
    switch (assetType) {
        case 'V':
            return VIDEO_SEARCH_ROUTE;
        case 'I':
            return IMAGE_SEARCH_ROUTE;
        case 'A':
            return AUDIO_SEARCH_ROUTE;
        default:
            return '';
    }
}

export const getContentTypeFromMediaType = (mediaType: SearchFilterMediaTypes): SearchFilterContentTypes => {
    switch (mediaType.toLowerCase()) {
        // Audio types
        case SearchFilterMediaTypes.All_audio_content_type:
            return SearchFilterContentTypes.All_audio_content_type;
        case SearchFilterMediaTypes.Music:
            return SearchFilterContentTypes.Music;
        case SearchFilterMediaTypes.Sound_effects:
            return SearchFilterContentTypes.Sound_effects;

        // Image types
        case SearchFilterMediaTypes.All_images_content_type:
            return SearchFilterContentTypes.All_images_content_type;
        case SearchFilterMediaTypes.Photos:
        case SearchFilterMediaTypes.Snapshots:
            return SearchFilterContentTypes.Photos;
        case SearchFilterMediaTypes.Vectors:
            return SearchFilterContentTypes.Vectors;
        case SearchFilterMediaTypes.Illustrations:
            return SearchFilterContentTypes.Illustrations;

        // Video types
        case SearchFilterMediaTypes.Video:
        case SearchFilterMediaTypes.Footage:
            return SearchFilterContentTypes.Footage;
        case SearchFilterMediaTypes.All_videos_content_type:
            return SearchFilterContentTypes.All_videos_content_type;
        case SearchFilterMediaTypes.Templates:
            return SearchFilterContentTypes.Templates;
        case SearchFilterMediaTypes.Motion_graphics:
        case SearchFilterMediaTypes.Backgrounds:
            return SearchFilterContentTypes.Motion_bgs;
        default:
            return SearchFilterContentTypes.All_videos_content_type;
    }
};

export const mapMediaTypeToSearchGatewayMediaType = (
    mediaType: SearchFilterMediaTypes,
    contentClass: ContentClassEnum,
): typeof SearchAudioMediaTypeEnum.Music | typeof SearchAudioMediaTypeEnum.Sfx | undefined => {
    switch (contentClass) {
        case 'audio':
            return audioMediaTypeToSearchGatewayMediaType[mediaType as 'audio' | 'music' | 'sound-effects'];
        // case('image'):
        // case('video'):
    }
};

export const audioMediaTypeToSearchGatewayMediaType = {
    [SearchFilterMediaTypes.All_audio_content_type]: undefined,
    [SearchFilterMediaTypes.Music]: SearchAudioMediaTypeEnum.Music,
    [SearchFilterMediaTypes.Sound_effects]: SearchAudioMediaTypeEnum.Sfx,
} as const;
